import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, TextField, Button, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Alert, Box, Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const SESSION_KEY = 'tablaHorasOperario_session';

const TablaHorasOperario = () => {
    const [operarios, setOperarios] = useState([]);
    const [operarioId, setOperarioId] = useState('');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Restaurar filtros y resultado desde sessionStorage al montar
    useEffect(() => {
        try {
            const saved = sessionStorage.getItem(SESSION_KEY);
            if (saved) {
                const { operarioId: oid, fechaInicio: fi, fechaFin: ff, resultado: res } = JSON.parse(saved);
                if (oid) setOperarioId(oid);
                if (fi) setFechaInicio(fi);
                if (ff) setFechaFin(ff);
                if (res) setResultado(res);
            }
        } catch (e) { /* silencio */ }
    }, []);

    useEffect(() => {
        const fetchOperarios = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await fetch('/api/operators', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) return;
                const data = await res.json();
                setOperarios(Array.isArray(data) ? data : []);
            } catch (e) {
                // silencio
            }
        };
        fetchOperarios();
    }, []);

    const handleConsultar = async () => {
        setError('');
        setResultado(null);
        if (!operarioId) { setError('Selecciona un operario.'); return; }
        if (!fechaInicio || !fechaFin) { setError('Ingresa las fechas de inicio y fin.'); return; }
        if (fechaFin < fechaInicio) { setError('La fecha final no puede ser anterior a la inicial.'); return; }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `/api/horas-operario?operarioId=${operarioId}&fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const data = await res.json();
            if (!res.ok) { setError(data.message || 'Error al consultar.'); return; }
            setResultado(data);
            // Guardar en sessionStorage
            try {
                sessionStorage.setItem(SESSION_KEY, JSON.stringify({ operarioId, fechaInicio, fechaFin, resultado: data }));
            } catch (e) { /* silencio */ }
        } catch (e) {
            setError('Error de conexión al servidor.');
        } finally {
            setLoading(false);
        }
    };

    const formatHoras = (horas, minutos) =>
        `${horas}h ${String(minutos).padStart(2, '0')}min`;

    const formatFecha = (fechaStr) => {
        const [y, m, d] = fechaStr.split('-');
        return `${d}/${m}/${y}`;
    };

    const handleExportPDF = () => {
        if (!resultado) return;
        const doc = new jsPDF();

        doc.setFontSize(14);
        doc.text('Tabla Horas Operario', 14, 16);
        doc.setFontSize(11);
        doc.text(`Operario: ${resultado.operario.name} ${resultado.operario.lastName}`, 14, 24);
        doc.text(`Período: ${formatFecha(fechaInicio)} — ${formatFecha(fechaFin)}`, 14, 31);

        const body = resultado.dias.map(d => [
            formatFecha(d.fecha),
            d.horaInicio && d.horaFin ? `${d.horaInicio} - ${d.horaFin}` : '—',
            formatHoras(d.horas, d.minutos)
        ]);
        body.push(['Total', '', formatHoras(resultado.totalGeneral.horas, resultado.totalGeneral.minutos)]);

        autoTable(doc, {
            startY: 37,
            head: [['Fecha', 'Horario', 'Horas y Minutos']],
            body,
            styles: { fontSize: 10 },
            headStyles: { fillColor: [25, 118, 210] },
            didParseCell: (data) => {
                if (data.row.index === body.length - 1) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [220, 220, 220];
                    data.cell.styles.textColor = [0, 0, 0];
                }
            }
        });

        doc.save(`horas_${resultado.operario.name}_${resultado.operario.lastName}_${fechaInicio}_${fechaFin}.pdf`);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Tabla Horas Operario
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Filtros */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            label="Operario"
                            value={operarioId}
                            onChange={e => {
                                setOperarioId(e.target.value);
                                try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ operarioId: e.target.value, fechaInicio, fechaFin, resultado })); } catch (err) { /* silencio */ }
                            }}
                            fullWidth
                            size="small"
                        >
                            <MenuItem value=""><em>Seleccionar...</em></MenuItem>
                            {operarios.map(op => (
                                <MenuItem key={op._id} value={op._id}>
                                    {op.name} {op.lastName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Fecha inicial"
                            type="date"
                            value={fechaInicio}
                            onChange={e => {
                                setFechaInicio(e.target.value);
                                try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ operarioId, fechaInicio: e.target.value, fechaFin, resultado })); } catch (err) { /* silencio */ }
                            }}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            label="Fecha final"
                            type="date"
                            value={fechaFin}
                            onChange={e => {
                                setFechaFin(e.target.value);
                                try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ operarioId, fechaInicio, fechaFin: e.target.value, resultado })); } catch (err) { /* silencio */ }
                            }}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<SearchIcon />}
                            onClick={handleConsultar}
                            disabled={loading}
                        >
                            {loading ? 'Consultando...' : 'Consultar'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Tabla resultado */}
            {resultado && (
                <TableContainer component={Paper}>
                    <Box sx={{ p: 2, backgroundColor: '#071525', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>
                            {resultado.operario.name} {resultado.operario.lastName}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPDF}
                            sx={{ color: '#fff', borderColor: '#fff', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' } }}
                        >
                            Exportar PDF
                        </Button>
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                                <TableCell sx={{ fontWeight: 'bold', color: '#000' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#000' }} align="center">Horario</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#000' }} align="right">
                                    Horas y Minutos
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {resultado.dias.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        Sin asignaciones en el rango seleccionado
                                    </TableCell>
                                </TableRow>
                            ) : (
                                resultado.dias.map(d => (
                                    <TableRow key={d.fecha} hover>
                                        <TableCell>{formatFecha(d.fecha)}</TableCell>
                                        <TableCell align="center">
                                            {d.horaInicio && d.horaFin
                                                ? `${d.horaInicio} - ${d.horaFin}`
                                                : '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatHoras(d.horas, d.minutos)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                            {resultado.dias.length > 0 && (
                                <TableRow sx={{ backgroundColor: '#bdbdbd' }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#000' }}>Total</TableCell>
                                    <TableCell />
                                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#000' }}>
                                        {formatHoras(resultado.totalGeneral.horas, resultado.totalGeneral.minutos)}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    );
};

export default TablaHorasOperario;
